import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@learn-me/ui";
import { adminApi } from "@/features/admin/api/adminApi";
import toast from "react-hot-toast";
import { UserPlus, Loader2, AlertCircle } from "lucide-react";

interface InviteStudentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function InviteStudentModal({
  open,
  onOpenChange,
}: InviteStudentModalProps) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [enrollType, setEnrollType] = useState<"none" | "course" | "bundle">(
    "none",
  );
  const [selectedCourseId, setSelectedCourseId] = useState<string>("");
  const [selectedBundleId, setSelectedBundleId] = useState<string>("");

  // Validación de email
  const emailError =
    emailTouched && email && !EMAIL_REGEX.test(email)
      ? "Ingresa un correo electrónico válido"
      : "";
  const isEmailValid = EMAIL_REGEX.test(email);

  // Fetch courses for selection
  const { data: coursesData, isLoading: isLoadingCourses } = useQuery({
    queryKey: ["admin-courses-for-invite"],
    queryFn: () => adminApi.getCourses(1, 100),
    enabled: open,
  });

  // Fetch bundles for selection
  const { data: bundlesData } = useQuery({
    queryKey: ["admin-bundles-for-invite"],
    queryFn: async () => {
      return { items: [] as Array<{ id: string; name: string }> };
    },
    enabled: open && enrollType === "bundle",
  });

  const createInvitation = useMutation({
    mutationFn: adminApi.createInvitation,
    onSuccess: () => {
      toast.success("Invitación enviada exitosamente");
      queryClient.invalidateQueries({ queryKey: ["admin-invitations"] });
      queryClient.invalidateQueries({ queryKey: ["admin-invitation-stats"] });
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Error al enviar la invitación");
    },
  });

  const handleClose = () => {
    setEmail("");
    setEmailTouched(false);
    setFirstName("");
    setLastName("");
    setEnrollType("none");
    setSelectedCourseId("");
    setSelectedBundleId("");
    onOpenChange(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!isEmailValid) {
      setEmailTouched(true);
      return;
    }

    createInvitation.mutate({
      email,
      firstName: firstName || undefined,
      lastName: lastName || undefined,
      autoEnrollCourseId:
        enrollType === "course" ? selectedCourseId : undefined,
      autoEnrollBundleId:
        enrollType === "bundle" ? selectedBundleId : undefined,
    });
  };

  // Determinar si el formulario es válido
  const isFormValid =
    isEmailValid &&
    (enrollType === "none" ||
      (enrollType === "course" && selectedCourseId) ||
      (enrollType === "bundle" && selectedBundleId));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary-600" />
            Invitar Estudiante
          </DialogTitle>
          <DialogDescription>
            Envía una invitación por correo para que el usuario cree su cuenta.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="invite-email">
              Correo electrónico <span className="text-red-500">*</span>
            </Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="usuario@ejemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
              required
              aria-invalid={!!emailError}
              aria-describedby={emailError ? "email-error" : undefined}
              className={
                emailError
                  ? "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                  : ""
              }
            />
            {emailError && (
              <p
                id="email-error"
                className="flex items-center gap-1.5 text-sm text-red-500"
                role="alert"
              >
                <AlertCircle className="h-3.5 w-3.5" />
                {emailError}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="invite-firstName">Nombre</Label>
              <Input
                id="invite-firstName"
                placeholder="Ej: María"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-lastName">Apellido</Label>
              <Input
                id="invite-lastName"
                placeholder="Ej: García"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="invite-enrollType">
              Pre-inscribir a (opcional)
            </Label>
            <Select
              value={enrollType}
              onValueChange={(v) => {
                setEnrollType(v as "none" | "course" | "bundle");
                setSelectedCourseId("");
                setSelectedBundleId("");
              }}
            >
              <SelectTrigger id="invite-enrollType">
                <SelectValue placeholder="Seleccionar tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin pre-inscripción</SelectItem>
                <SelectItem value="course">Curso</SelectItem>
                <SelectItem value="bundle" disabled>
                  Paquete (próximamente)
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {enrollType === "course" && (
            <div className="space-y-2">
              <Label htmlFor="invite-course">
                Seleccionar curso <span className="text-red-500">*</span>
              </Label>
              {isLoadingCourses ? (
                <div className="h-10 w-full rounded-md bg-gray-100 animate-pulse" />
              ) : coursesData?.items && coursesData.items.length > 0 ? (
                <Select
                  value={selectedCourseId}
                  onValueChange={setSelectedCourseId}
                >
                  <SelectTrigger id="invite-course">
                    <SelectValue placeholder="Selecciona un curso" />
                  </SelectTrigger>
                  <SelectContent>
                    {coursesData.items.map((course) => (
                      <SelectItem key={course.id} value={course.id}>
                        {course.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-gray-500 py-2">
                  No hay cursos disponibles para pre-inscripción.
                </p>
              )}
            </div>
          )}

          {enrollType === "bundle" && bundlesData?.items && (
            <div className="space-y-2">
              <Label htmlFor="invite-bundle">Seleccionar paquete</Label>
              <p className="text-sm text-gray-500 py-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                La pre-inscripción a paquetes estará disponible próximamente.
              </p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={createInvitation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={!isFormValid || createInvitation.isPending}
            >
              {createInvitation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Enviar Invitación
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
